// Field descriptors for the employee forms (see hrForm.jsx). They mirror the API's
// employees.config.js; the API stays the judge of what is valid.
export const GENDER_OPTIONS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Other", value: "OTHER" },
];
export const MARITAL_OPTIONS = ["SINGLE", "MARRIED", "DIVORCED", "WIDOWED"].map((v) => ({ label: v.charAt(0) + v.slice(1).toLowerCase(), value: v }));
export const EMPLOYMENT_TYPE_OPTIONS = [
  ["PERMANENT", "Permanent"],
  ["CONTRACT", "Contract"],
  ["PROBATION", "Probation"],
  ["INTERN", "Intern"],
  ["PART_TIME", "Part time"],
  ["DAILY_WAGE", "Daily wage"],
].map(([value, label]) => ({ label, value }));
// "Admin" is a platform-wide role, not an HR one - picking it grants full tenant-admin rights everywhere on the
// platform (every permission check, in HR and outside it, passes for them), not just inside HR. See the warning
// EmployeeAddDrawer shows when it's selected.
// There is deliberately no default: the role decides what this person can do across the platform, so whoever
// adds them chooses it. The values are ezp_role ids (and ezp_users.role).
export const LOGIN_ROLE_OPTIONS = [
  { label: "Admin - full access to the whole platform", value: 2 },
  { label: "Support - office / support staff", value: 6 },
  { label: "HR - HR department staff (HR screens only)", value: 7 },
  { label: "Crew - field crew / service provider", value: 5 },
];
// Only the owner or an Admin can make an Admin; HR staff can add the others.
export const roleOptionsFor = (canMakeAdmin) => LOGIN_ROLE_OPTIONS.filter((o) => canMakeAdmin || o.value !== 2);

// After creation a login can be switched among these (mirrors the API's SWITCHABLE_ROLES). Crew and the
// owner stay as they are: Crew has bookings and shifts attached, and the owner is not an employee role.
export const SWITCHABLE_LOGIN_ROLES = [2, 6, 7];
export const roleChangeOptions = () => LOGIN_ROLE_OPTIONS.filter((o) => SWITCHABLE_LOGIN_ROLES.includes(o.value));

// Admin, Support and HR logins sign in with the work phone / work email on the profile (the API keeps them in
// step); the owner's and crew's logins are managed elsewhere.
export const loginFollowsProfile = (loginRole) => SWITCHABLE_LOGIN_ROLES.includes(Number(loginRole));
export const SIGN_IN_HINT = "Also used to sign in. A change applies from their next sign-in.";

const signedInUserId = () => {
  try {
    return String(localStorage.getItem("userId") || "").replace(/"/g, "");
  } catch {
    return "";
  }
};

// Whether the Role select is offered in the Edit employee form. The API is the judge (owner / Admin only, never
// your own role, never a leaver); this only keeps the control out of the way where it could not work.
export const canEditLoginRole = ({ isTenantAdmin, loginRole, status, employeeId }) =>
  !!isTenantAdmin && loginFollowsProfile(loginRole) && status !== "SEPARATED" && !(signedInUserId() && signedInUserId() === String(employeeId));

// What to tell the person who saved, when the save changed how someone signs in or what they can open.
export const signInChangeNotice = (res) => {
  const d = (res && res.data) || {};
  const parts = [];
  if (d.login_updated && d.login_updated.phone) parts.push("mobile number");
  if (d.login_updated && d.login_updated.email) parts.push("email");
  if (d.role_changed) parts.push("role");
  return parts.length ? `Their sign-in ${parts.join(" / ")} changed; it applies from their next sign-in.` : "";
};

export const personalFields = () => [
  { key: "full_name", label: "Full name", type: "text", required: true, max: 150 },
  { key: "work_email", label: "Work email", type: "email", max: 150 },
  // Country code + digits, like the booking screen: the number is also the sign-in for Admin / Support / HR.
  { key: "work_phone", label: "Work phone", type: "mobile" },
  { key: "gender", label: "Gender", type: "select", options: GENDER_OPTIONS },
  { key: "date_of_birth", label: "Date of birth", type: "date" },
  { key: "nationality", label: "Nationality", type: "text", max: 60 },
  { key: "marital_status", label: "Marital status", type: "select", options: MARITAL_OPTIONS },
  { key: "current_address", label: "Current address", type: "textarea", max: 500, rows: 2 },
  { key: "permanent_address", label: "Permanent address", type: "textarea", max: 500, rows: 2 },
];

export const employmentFields = () => [
  { key: "date_of_joining", label: "Date of joining", type: "date", required: true },
  { key: "probation_end_date", label: "Probation ends", type: "date" },
  { key: "confirmation_date", label: "Confirmation date", type: "date" },
  { key: "notice_period_days", label: "Notice period (days)", type: "number", min: 0 },
];

// `opts`: { departments, designations, grades, locations, sites, "cost-centres", "leave-policies" } option lists.
export const jobFields = (opts = {}, { excludeEmployee = [] } = {}) => [
  { key: "department_id", label: "Department", type: "select", options: opts.departments || [] },
  { key: "designation_id", label: "Designation", type: "select", options: opts.designations || [] },
  { key: "grade_id", label: "Grade", type: "select", options: opts.grades || [] },
  { key: "location_id", label: "Location", type: "select", options: opts.locations || [] },
  { key: "site_id", label: "Site", type: "select", options: opts.sites || [] },
  { key: "cost_centre_id", label: "Cost centre", type: "select", options: opts["cost-centres"] || [] },
  { key: "manager_id", label: "Reports to", type: "employee", exclude: excludeEmployee },
  { key: "employment_type", label: "Employment type", type: "select", options: EMPLOYMENT_TYPE_OPTIONS },
  { key: "leave_policy_id", label: "Leave policy", type: "select", options: opts["leave-policies"] || [] },
];

// The Employees tier (plans other than the HR plan) captures who the person is and the basic job
// details; terms like probation, grade, cost centre or leave policy belong to the full HR module.
export const liteEmploymentFields = () => employmentFields().filter((f) => f.key === "date_of_joining");
export const liteJobFields = (opts = {}) =>
  jobFields(opts).filter((f) => ["department_id", "designation_id", "location_id", "employment_type"].includes(f.key));
export const LITE_JOB_MASTER_KEYS = ["departments", "designations", "locations"];

export const JOB_MASTER_KEYS = ["departments", "designations", "grades", "locations", "sites", "cost-centres", "leave-policies"];
